/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type {
  GoogleLoginData,
  GoogleTokenValidationResult,
  FacebookLoginData,
  FacebookTokenValidationResult,
  AppleLoginData,
  AppleTokenValidationResult,
} from '../widgets/LoginView/types';
import * as AppleAuthentication from 'expo-apple-authentication';

/**
 * Utility function to validate Google login data
 * Ensures all required fields are present for successful authentication
 */
export const validateGoogleLoginData = (
  data: GoogleLoginData,
): GoogleTokenValidationResult => {
  if (!data) {
    return { isValid: false, error: 'Google login data is required' };
  }

  if (!data.user || !data.user.id) {
    return {
      isValid: false,
      error: 'User ID is required in Google login data',
    };
  }

  if (!data.idToken && !data.serverAuthCode) {
    return {
      isValid: false,
      error: 'Either ID token or server auth code is required',
    };
  }

  if (!data.googleClientId) {
    return {
      isValid: false,
      error: 'Google client ID is required',
    };
  }

  return { isValid: true };
};

/**
 * Utility function to validate Facebook login data
 * Ensures all required fields are present for successful authentication
 */
export const validateFacebookLoginData = (
  data: FacebookLoginData,
): FacebookTokenValidationResult => {
  if (!data) {
    return { isValid: false, error: 'Facebook login data is required' };
  }

  if (!data.user || !data.user.id) {
    return {
      isValid: false,
      error: 'User ID is required in Facebook login data',
    };
  }

  if (!data.accessToken) {
    return {
      isValid: false,
      error: 'Access token is required',
    };
  }

  return { isValid: true };
};

// Utility function to validate Apple login data
export const validateAppleLoginData = (
  data: AppleLoginData,
): AppleTokenValidationResult => {
  if (!data) {
    return { isValid: false, error: 'Apple login data is required' };
  }

  if (!data.user || !data.user.id) {
    return {
      isValid: false,
      error: 'Apple ID is required in Apple login data',
    };
  }

  if (!data.authCode) {
    return {
      isValid: false,
      error: 'Authorization code is required',
    };
  }

  return { isValid: true };
};

const CANCELLED_SOCIAL_AUTH_MARKERS = [
  'SIGN_IN_CANCELLED',
  'POPUP_CLOSED',
  'ERR_REQUEST_CANCELED',
  'ERR_CANCELED',
  'error 1001',
  'authorizationerror',
  'cancelled',
  'canceled',
  'user cancelled',
  'user canceled',
];

// User dismissed Google / Apple / passkey sheet — not a connection failure.
export const isSocialAuthCancelled = (error: unknown): boolean => {
  if (error == null) return false;

  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code?: unknown }).code ?? '')
      : '';
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(error);

  // ASAuthorizationError.canceled === 1001 (passkey / Apple Auth Services)
  if (code === '1001' || code === '-1') {
    return true;
  }

  const haystack = `${code} ${message}`.toLowerCase();
  return CANCELLED_SOCIAL_AUTH_MARKERS.some((marker) =>
    haystack.includes(marker.toLowerCase()),
  );
};

export const appleSignIn = async (): Promise<AppleLoginData> => {
  const credential = await AppleAuthentication.signInAsync({
    requestedScopes: [
      AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
      AppleAuthentication.AppleAuthenticationScope.EMAIL,
    ],
  });

  return {
    user: {
      id: credential.user,
      email: credential.email ?? null,
      firstName: credential.fullName?.givenName ?? null,
      lastName: credential.fullName?.familyName ?? null,
    },
    authCode: credential.authorizationCode ?? null,
    identityToken: credential.identityToken ?? null,
  };
};
